import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from '../components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  CalendarDays,
  ClipboardCheck,
  AlertTriangle,
  Gauge,
  CheckCircle,
  XCircle,
  Clock,
  User,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Filter,
  Download,
  Eye,
  Plus,
  PlusCircle,
  Shield,
  Loader2
} from 'lucide-react';
import { fr } from 'date-fns/locale';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { calculerTRS } from '../utils/trsCalcul';
import { ExportButtons } from '../components/ExportButtons';
import { useAuth } from '../contexts/AuthContext';

import { API_BASE } from '../utils/apiClient';

const API = API_BASE;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const EventHistory = () => {
  const { i18n } = useTranslation();
  const { user, isAdmin, isDemoReadonly } = useAuth();
  
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState({
    tours: [],
    incidents: [],
    trs_entries: [],
    actions: []
  });
  const [dayEvents, setDayEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventDates, setEventDates] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Admin data entry
  const [addDataDialogOpen, setAddDataDialogOpen] = useState(false);
  const [addDataType, setAddDataType] = useState('trs');
  const [submitting, setSubmitting] = useState(false);
  
  // Reference data
  const [zones, setZones] = useState([]);
  const [sites, setSites] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Form data for different types
  const [trsForm, setTrsForm] = useState({
    zone_id: '',
    shift: 'Jour',
    planned_time: 480,
    downtime: 0,
    theoretical_cadence: 100,
    actual_cadence: 0,
    total_production: 0,
    conforming_production: 0,
    rejects: 0
  });
  
  const [tourForm, setTourForm] = useState({
    zone_id: '',
    checklist_id: '',
    shift: 'Jour',
    supervisor_id: '',
    ok_count: 0,
    nok_count: 0,
    na_count: 0,
    compliance_rate: 0
  });
  
  const [incidentForm, setIncidentForm] = useState({
    site_id: '',
    incident_type: 'presqu_accident',
    severity: 'mineur',
    description: '',
    cause: '',
    days_lost: 0,
    corrective_action: ''
  });
  
  const [actionForm, setActionForm] = useState({
    title: '',
    description: '',
    zone_id: '',
    priority: 'moyenne',
    status: 'A_FAIRE',
    responsible_id: '',
    deadline: ''
  });

  useEffect(() => {
    fetchMonthEvents();
    fetchReferenceData();
  }, [currentMonth]);

  // Re-filter day events when events data changes or date selection changes
  useEffect(() => {
    if (selectedDate) {
      fetchDayEvents(selectedDate);
    }
  }, [selectedDate, events.tours, events.incidents, events.trs_entries, events.actions]);
  
  const fetchReferenceData = async () => {
    try {
      const [zonesRes, sitesRes, checklistsRes, usersRes] = await Promise.all([
        axios.get(`${API}/zones`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/sites`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/checklists`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/users`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      setZones(zonesRes.data || []);
      setSites(sitesRes.data || []);
      setChecklists(checklistsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const fetchMonthEvents = async () => {
    try {
      const start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const end = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      
      console.log('Fetching month events from', start, 'to', end);
      
      const [toursRes, incidentsRes, trsRes, actionsRes] = await Promise.all([
        axios.get(`${API}/tours?start_date=${start}&end_date=${end}`, { headers: getAuthHeaders() }).catch((e) => { console.error('Tours error:', e); return { data: [] }; }),
        axios.get(`${API}/safety/incidents`, { headers: getAuthHeaders() }).catch((e) => { console.error('Incidents error:', e); return { data: [] }; }),
        axios.get(`${API}/trs-entries`, { headers: getAuthHeaders() }).catch((e) => { console.error('TRS error:', e); return { data: [] }; }),
        axios.get(`${API}/actions`, { headers: getAuthHeaders() }).catch((e) => { console.error('Actions error:', e); return { data: [] }; })
      ]);

      console.log('API responses:', {
        tours: toursRes.data?.length || 0,
        incidents: incidentsRes.data?.length || 0,
        trs: trsRes.data?.length || 0,
        actions: actionsRes.data?.length || 0
      });

      // Build a map of dates with events
      const dateMap = {};
      
      // Tours
      (toursRes.data || []).forEach(tour => {
        const date = tour.created_at?.split('T')[0];
        if (date) {
          if (!dateMap[date]) dateMap[date] = { tours: 0, incidents: 0, trs: 0, actions: 0 };
          dateMap[date].tours++;
        }
      });

      // Incidents
      (incidentsRes.data || []).forEach(incident => {
        const date = incident.incident_date?.split('T')[0] || incident.created_at?.split('T')[0];
        if (date) {
          if (!dateMap[date]) dateMap[date] = { tours: 0, incidents: 0, trs: 0, actions: 0 };
          dateMap[date].incidents++;
        }
      });

      // TRS entries
      (trsRes.data || []).forEach(entry => {
        const date = entry.date?.split('T')[0];
        if (date) {
          if (!dateMap[date]) dateMap[date] = { tours: 0, incidents: 0, trs: 0, actions: 0 };
          dateMap[date].trs++;
        }
      });

      // Actions (by creation date)
      (actionsRes.data || []).forEach(action => {
        const date = action.created_at?.split('T')[0];
        if (date) {
          if (!dateMap[date]) dateMap[date] = { tours: 0, incidents: 0, trs: 0, actions: 0 };
          dateMap[date].actions++;
        }
      });

      setEventDates(dateMap);
      setEvents({
        tours: toursRes.data || [],
        incidents: incidentsRes.data || [],
        trs_entries: trsRes.data || [],
        actions: actionsRes.data || []
      });
    } catch (error) {
      console.error('Error fetching month events:', error);
    }
  };

  const fetchDayEvents = async (date) => {
    setLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      
      // Helper function to check if a date string matches the selected date
      const matchesDate = (dateField) => {
        if (!dateField) return false;
        // Handle both ISO format with timezone and simple date format
        const fieldDate = dateField.split('T')[0];
        return fieldDate === dateStr;
      };
      
      // Filter events for the selected date
      const dayTours = events.tours.filter(t => matchesDate(t.created_at));
      const dayIncidents = events.incidents.filter(i => 
        matchesDate(i.incident_date) || matchesDate(i.created_at)
      );
      const dayTrs = events.trs_entries.filter(e => matchesDate(e.date) || matchesDate(e.created_at));
      const dayActions = events.actions.filter(a => matchesDate(a.created_at));

      // Debug logging
      console.log('fetchDayEvents for:', dateStr);
      console.log('Total tours:', events.tours.length, 'Filtered:', dayTours.length);
      console.log('Total incidents:', events.incidents.length, 'Filtered:', dayIncidents.length);
      console.log('Total TRS:', events.trs_entries.length, 'Filtered:', dayTrs.length);
      console.log('Total actions:', events.actions.length, 'Filtered:', dayActions.length);

      // Combine and sort by time
      const allEvents = [
        ...dayTours.map(t => ({ ...t, type: 'tour', time: t.created_at })),
        ...dayIncidents.map(i => ({ ...i, type: 'incident', time: i.created_at })),
        ...dayTrs.map(e => ({ ...e, type: 'trs', time: e.created_at })),
        ...dayActions.map(a => ({ ...a, type: 'action', time: a.created_at }))
      ].sort((a, b) => new Date(b.time) - new Date(a.time));

      setDayEvents(allEvents);
    } catch (error) {
      console.error('Error fetching day events:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Admin: Submit manual data
  const handleSubmitManualData = async () => {
    setSubmitting(true);
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const dateISO = new Date(dateStr + 'T12:00:00Z').toISOString();
    
    try {
      switch (addDataType) {
        case 'trs':
          if (!trsForm.zone_id) {
            toast.error('Veuillez sélectionner une zone');
            setSubmitting(false);
            return;
          }
          {
            const controle = calculerTRS(trsForm);
            if (controle.erreur) {
              toast.error(controle.erreur);
              setSubmitting(false);
              return;
            }
          }
          // Correctif 19/10/2026 : la route /trs n'existait pas (erreur 404) ; le serveur calcule le TRS
          await axios.post(`${API}/trs-entries`, {
            zone_id: trsForm.zone_id,
            date: dateStr,
            shift: trsForm.shift,
            planned_time: parseFloat(trsForm.planned_time) || 0,
            downtime: parseFloat(trsForm.downtime) || 0,
            total_production: parseFloat(trsForm.total_production) || 0,
            conforming_production: parseFloat(trsForm.conforming_production) || 0,
            theoretical_cadence: parseFloat(trsForm.theoretical_cadence) || 0
          }, { headers: getAuthHeaders() });
          toast.success('Données TRS enregistrées');
          break;
          
        case 'tour':
          if (!tourForm.zone_id || !tourForm.checklist_id) {
            toast.error('Veuillez sélectionner une zone et une checklist');
            setSubmitting(false);
            return;
          }
          const total = tourForm.ok_count + tourForm.nok_count + tourForm.na_count;
          const compliance = total > 0 ? Math.round((tourForm.ok_count / total) * 100) : 0;
          
          // Find supervisor name
          const supervisor = users.find(u => u.id === tourForm.supervisor_id);
          
          // Use admin endpoint for manual entry with specific date
          await axios.post(`${API}/admin/tours/manual`, {
            zone_id: tourForm.zone_id,
            checklist_id: tourForm.checklist_id,
            shift: tourForm.shift,
            supervisor_id: tourForm.supervisor_id,
            supervisor_name: supervisor?.name || 'Admin',
            ok_count: parseInt(tourForm.ok_count),
            nok_count: parseInt(tourForm.nok_count),
            na_count: parseInt(tourForm.na_count),
            compliance_rate: compliance,
            lean_score: compliance,
            manual_date: dateISO
          }, { headers: getAuthHeaders() });
          toast.success('Tournée enregistrée');
          break;
          
        case 'incident':
          if (!incidentForm.site_id || !incidentForm.description) {
            toast.error('Veuillez remplir le site et la description');
            setSubmitting(false);
            return;
          }
          await axios.post(`${API}/safety/incidents`, {
            ...incidentForm,
            incident_date: dateISO
          }, { headers: getAuthHeaders() });
          toast.success('Incident enregistré');
          break;
          
        case 'action':
          if (!actionForm.title) {
            toast.error('Veuillez remplir le titre');
            setSubmitting(false);
            return;
          }
          const responsibleUser = users.find(u => u.id === actionForm.responsible_id);
          // Use admin endpoint for manual entry with specific date
          await axios.post(`${API}/admin/actions/manual`, {
            title: actionForm.title,
            description: actionForm.description,
            zone_id: actionForm.zone_id,
            priority: actionForm.priority,
            status: actionForm.status,
            responsible_id: actionForm.responsible_id,
            responsible_name: responsibleUser?.name || '',
            deadline: actionForm.deadline,
            manual_date: dateISO
          }, { headers: getAuthHeaders() });
          toast.success('Action enregistrée');
          break;
      }
      
      // Refresh data
      await fetchMonthEvents();
      setAddDataDialogOpen(false);
      resetForms();
      
    } catch (error) {
      console.error('Error submitting data:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };
  
  const resetForms = () => {
    setTrsForm({
      zone_id: '',
      shift: 'Jour',
      planned_time: 480,
      downtime: 0,
      theoretical_cadence: 100,
      actual_cadence: 0,
      total_production: 0,
      conforming_production: 0,
      rejects: 0
    });
    setTourForm({
      zone_id: '',
      checklist_id: '',
      shift: 'Jour',
      supervisor_id: '',
      ok_count: 0,
      nok_count: 0,
      na_count: 0,
      compliance_rate: 0
    });
    setIncidentForm({
      site_id: '',
      incident_type: 'presqu_accident',
      severity: 'mineur',
      description: '',
      cause: '',
      days_lost: 0,
      corrective_action: ''
    });
    setActionForm({
      title: '',
      description: '',
      zone_id: '',
      priority: 'moyenne',
      status: 'A_FAIRE',
      responsible_id: '',
      deadline: ''
    });
  };

  const filteredEvents = activeFilter === 'all' 
    ? dayEvents 
    : dayEvents.filter(e => e.type === activeFilter);

  const getEventIcon = (type) => {
    switch (type) {
      case 'tour': return <ClipboardCheck className="w-5 h-5 text-blue-600" />;
      case 'incident': return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case 'trs': return <Gauge className="w-5 h-5 text-purple-600" />;
      case 'action': return <CheckCircle className="w-5 h-5 text-green-600" />;
      default: return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getEventBadge = (type) => {
    switch (type) {
      case 'tour': return <Badge className="bg-blue-100 text-blue-800">Tournée</Badge>;
      case 'incident': return <Badge className="bg-red-100 text-red-800">Incident</Badge>;
      case 'trs': return <Badge className="bg-purple-100 text-purple-800">TRS</Badge>;
      case 'action': return <Badge className="bg-green-100 text-green-800">Action</Badge>;
      default: return <Badge>Événement</Badge>;
    }
  };

  const renderEventCard = (event) => {
    const time = event.time ? format(parseISO(event.time), 'HH:mm', { locale: fr }) : '--:--';
    
    switch (event.type) {
      case 'tour':
        return (
          <div className="flex items-start gap-4 p-4 bg-white rounded-lg border hover:shadow-md transition-shadow">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              {getEventIcon('tour')}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {getEventBadge('tour')}
                <span className="text-sm text-slate-500">{time}</span>
                {event.status === 'a_analyser' && (
                  <Badge className="bg-amber-100 text-amber-800">À analyser</Badge>
                )}
                {event.status === 'verrouillee' && (
                  <Badge className="bg-green-100 text-green-800">Validée</Badge>
                )}
              </div>
              <h4 className="font-semibold text-slate-800 truncate">
                Tournée terrain
              </h4>
              <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  {event.ok_count || 0} OK
                </span>
                <span className="flex items-center gap-1">
                  <XCircle className="w-4 h-4 text-red-500" />
                  {event.nok_count || 0} NOK
                </span>
                {event.compliance_rate > 0 && (
                  <span className="font-medium">
                    {event.compliance_rate}% conformité
                  </span>
                )}
              </div>
            </div>
          </div>
        );

      case 'incident':
        return (
          <div className="flex items-start gap-4 p-4 bg-white rounded-lg border border-red-200 hover:shadow-md transition-shadow">
            <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              {getEventIcon('incident')}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {getEventBadge('incident')}
                <span className="text-sm text-slate-500">{time}</span>
                <Badge className={`${event.with_stop ? 'bg-red-600 text-white' : 'bg-yellow-100 text-yellow-800'}`}>
                  {event.with_stop ? 'Avec arrêt' : 'Sans arrêt'}
                </Badge>
              </div>
              <h4 className="font-semibold text-slate-800">
                {event.type_incident || event.incident_type || 'Incident de sécurité'}
              </h4>
              <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                {event.description || 'Pas de description'}
              </p>
            </div>
          </div>
        );

      case 'trs':
        return (
          <div className="flex items-start gap-4 p-4 bg-white rounded-lg border hover:shadow-md transition-shadow">
            <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              {getEventIcon('trs')}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {getEventBadge('trs')}
                <span className="text-sm text-slate-500">{event.shift || 'Jour'}</span>
              </div>
              <h4 className="font-semibold text-slate-800">
                Saisie TRS/OEE
              </h4>
              <div className="flex items-center gap-4 mt-2 text-sm">
                <span className="px-2 py-1 bg-blue-50 rounded">
                  Dispo: {event.availability?.toFixed(1) || 0}%
                </span>
                <span className="px-2 py-1 bg-green-50 rounded">
                  Perf: {event.performance?.toFixed(1) || 0}%
                </span>
                <span className="px-2 py-1 bg-purple-50 rounded font-semibold">
                  TRS: {event.oee?.toFixed(1) || 0}%
                </span>
              </div>
            </div>
          </div>
        );

      case 'action':
        return (
          <div className="flex items-start gap-4 p-4 bg-white rounded-lg border hover:shadow-md transition-shadow">
            <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              {getEventIcon('action')}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {getEventBadge('action')}
                <span className="text-sm text-slate-500">{time}</span>
                <Badge className={`${
                  event.status === 'FAIT' ? 'bg-green-100 text-green-800' :
                  event.status === 'EN_COURS' ? 'bg-blue-100 text-blue-800' :
                  'bg-slate-100 text-slate-800'
                }`}>
                  {event.status || 'À faire'}
                </Badge>
              </div>
              <h4 className="font-semibold text-slate-800 truncate">
                {event.title || 'Action corrective'}
              </h4>
              <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                {event.description || 'Pas de description'}
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Custom day renderer for calendar
  const renderDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayData = eventDates[dateStr];
    
    if (!dayData) return null;

    const total = dayData.tours + dayData.incidents + dayData.trs + dayData.actions;
    if (total === 0) return null;

    return (
      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 flex gap-0.5">
        {dayData.tours > 0 && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
        {dayData.incidents > 0 && <div className="w-1.5 h-1.5 rounded-full bg-red-500" />}
        {dayData.trs > 0 && <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />}
        {dayData.actions > 0 && <div className="w-1.5 h-1.5 rounded-full bg-green-500" />}
      </div>
    );
  };

  // Filtered checklists by zone
  const filteredChecklists = tourForm.zone_id 
    ? checklists.filter(c => c.zone_id === tourForm.zone_id)
    : checklists;

  return (
    <div className="p-6 space-y-6" data-testid="event-history-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2" data-testid="page-title">
            <CalendarDays className="w-7 h-7 text-blue-600" />
            Historique des événements
          </h1>
          <p className="text-slate-600 mt-1">
            Consultez tous les événements par date
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin() && !isDemoReadonly() && (
            <Button
              onClick={() => setAddDataDialogOpen(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
              data-testid="admin-add-data-btn"
            >
              <Shield className="w-4 h-4" />
              <PlusCircle className="w-4 h-4" />
              Saisie manuelle
            </Button>
          )}
          <ExportButtons 
            module="tours" 
            filters={{
              start_date: format(startOfMonth(currentMonth), 'yyyy-MM-dd'),
              end_date: format(endOfMonth(currentMonth), 'yyyy-MM-dd')
            }}
            language={i18n.language || 'fr'}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Calendrier</CardTitle>
            <CardDescription>
              Sélectionnez une date pour voir les événements
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              onMonthChange={setCurrentMonth}
              locale={fr}
              className="rounded-md border"
              data-testid="event-calendar"
              components={{
                DayContent: ({ date }) => (
                  <div className="relative w-full h-full flex items-center justify-center">
                    <span>{format(date, 'd')}</span>
                    {renderDay(date)}
                  </div>
                )
              }}
            />

            {/* Legend */}
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium text-slate-700 mb-2">Légende</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <span>Tournées</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span>Incidents</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500" />
                  <span>TRS</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span>Actions</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Events List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">
                  Événements du {format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })}
                </CardTitle>
                <CardDescription>
                  {filteredEvents.length} événement(s)
                </CardDescription>
              </div>
              {isAdmin() && filteredEvents.length === 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAddDataDialogOpen(true)}
                  className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter des données
                </Button>
              )}
            </div>

            {/* Filters */}
            <div className="flex gap-2 mt-4 flex-wrap">
              <Button
                variant={activeFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveFilter('all')}
              >
                Tous ({dayEvents.length})
              </Button>
              <Button
                variant={activeFilter === 'tour' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveFilter('tour')}
                className="gap-1"
              >
                <ClipboardCheck className="w-4 h-4" />
                Tournées ({dayEvents.filter(e => e.type === 'tour').length})
              </Button>
              <Button
                variant={activeFilter === 'incident' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveFilter('incident')}
                className="gap-1"
              >
                <AlertTriangle className="w-4 h-4" />
                Incidents ({dayEvents.filter(e => e.type === 'incident').length})
              </Button>
              <Button
                variant={activeFilter === 'trs' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveFilter('trs')}
                className="gap-1"
              >
                <Gauge className="w-4 h-4" />
                TRS ({dayEvents.filter(e => e.type === 'trs').length})
              </Button>
              <Button
                variant={activeFilter === 'action' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveFilter('action')}
                className="gap-1"
              >
                <CheckCircle className="w-4 h-4" />
                Actions ({dayEvents.filter(e => e.type === 'action').length})
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredEvents.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <CalendarDays className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Aucun événement pour cette date</p>
                {isAdmin() && (
                  <Button
                    variant="link"
                    onClick={() => setAddDataDialogOpen(true)}
                    className="mt-2 text-amber-600"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter des données manuellement
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                {filteredEvents.map((event, index) => (
                  <div key={`${event.type}-${event.id || index}`}>
                    {renderEventCard(event)}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <ClipboardCheck className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-800">
                  {events.tours.length}
                </p>
                <p className="text-sm text-blue-600">Tournées ce mois</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-800">
                  {events.incidents.length}
                </p>
                <p className="text-sm text-red-600">Incidents ce mois</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Gauge className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold text-purple-800">
                  {events.trs_entries.length}
                </p>
                <p className="text-sm text-purple-600">Saisies TRS</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-800">
                  {events.actions.length}
                </p>
                <p className="text-sm text-green-600">Actions ce mois</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Admin: Add Data Dialog */}
      <Dialog open={addDataDialogOpen} onOpenChange={setAddDataDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Shield className="w-5 h-5" />
              Saisie manuelle de données (Admin)
            </DialogTitle>
            <DialogDescription>
              Ajouter des données pour le <strong>{format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Data Type Selector */}
            <div>
              <Label>Type de données à saisir</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                <Button
                  variant={addDataType === 'trs' ? 'default' : 'outline'}
                  onClick={() => setAddDataType('trs')}
                  className={addDataType === 'trs' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                >
                  <Gauge className="w-4 h-4 mr-1" />
                  TRS
                </Button>
                <Button
                  variant={addDataType === 'tour' ? 'default' : 'outline'}
                  onClick={() => setAddDataType('tour')}
                  className={addDataType === 'tour' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                >
                  <ClipboardCheck className="w-4 h-4 mr-1" />
                  Tournée
                </Button>
                <Button
                  variant={addDataType === 'incident' ? 'default' : 'outline'}
                  onClick={() => setAddDataType('incident')}
                  className={addDataType === 'incident' ? 'bg-red-600 hover:bg-red-700' : ''}
                >
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Incident
                </Button>
                <Button
                  variant={addDataType === 'action' ? 'default' : 'outline'}
                  onClick={() => setAddDataType('action')}
                  className={addDataType === 'action' ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Action
                </Button>
              </div>
            </div>
            
            {/* TRS Form */}
            {addDataType === 'trs' && (
              <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 flex items-center gap-2">
                  <Gauge className="w-5 h-5" />
                  Saisie TRS/OEE
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Zone/Machine *</Label>
                    <Select value={trsForm.zone_id} onValueChange={(v) => setTrsForm({...trsForm, zone_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                      <SelectContent>
                        {zones.map(z => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Quart</Label>
                    <Select value={trsForm.shift} onValueChange={(v) => setTrsForm({...trsForm, shift: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Jour">Jour</SelectItem>
                        <SelectItem value="Soir">Soir</SelectItem>
                        <SelectItem value="Nuit">Nuit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Temps planifié (min)</Label>
                    <Input type="number" value={trsForm.planned_time} onChange={(e) => setTrsForm({...trsForm, planned_time: parseInt(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Temps d'arrêt (min)</Label>
                    <Input type="number" value={trsForm.downtime} onChange={(e) => setTrsForm({...trsForm, downtime: parseInt(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Cadence théorique</Label>
                    <Input type="number" value={trsForm.theoretical_cadence} onChange={(e) => setTrsForm({...trsForm, theoretical_cadence: parseInt(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Cadence réelle</Label>
                    <Input type="number" value={trsForm.actual_cadence} onChange={(e) => setTrsForm({...trsForm, actual_cadence: parseInt(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Production totale</Label>
                    <Input type="number" value={trsForm.total_production} onChange={(e) => setTrsForm({...trsForm, total_production: parseInt(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Production conforme</Label>
                    <Input type="number" value={trsForm.conforming_production} onChange={(e) => setTrsForm({...trsForm, conforming_production: parseInt(e.target.value) || 0})} />
                  </div>
                </div>
              </div>
            )}
            
            {/* Tour Form */}
            {addDataType === 'tour' && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5" />
                  Saisie Tournée (résumé)
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Zone *</Label>
                    <Select value={tourForm.zone_id} onValueChange={(v) => setTourForm({...tourForm, zone_id: v, checklist_id: ''})}>
                      <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                      <SelectContent>
                        {zones.map(z => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Checklist *</Label>
                    <Select value={tourForm.checklist_id} onValueChange={(v) => setTourForm({...tourForm, checklist_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                      <SelectContent>
                        {filteredChecklists.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Superviseur</Label>
                    <Select value={tourForm.supervisor_id} onValueChange={(v) => setTourForm({...tourForm, supervisor_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                      <SelectContent>
                        {users.filter(u => u.role === 'superviseur' || u.role === 'admin').map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Quart</Label>
                    <Select value={tourForm.shift} onValueChange={(v) => setTourForm({...tourForm, shift: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Jour">Jour</SelectItem>
                        <SelectItem value="Soir">Soir</SelectItem>
                        <SelectItem value="Nuit">Nuit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Points OK</Label>
                    <Input type="number" value={tourForm.ok_count} onChange={(e) => setTourForm({...tourForm, ok_count: parseInt(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Points NOK</Label>
                    <Input type="number" value={tourForm.nok_count} onChange={(e) => setTourForm({...tourForm, nok_count: parseInt(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Points N/A</Label>
                    <Input type="number" value={tourForm.na_count} onChange={(e) => setTourForm({...tourForm, na_count: parseInt(e.target.value) || 0})} />
                  </div>
                </div>
              </div>
            )}
            
            {/* Incident Form */}
            {addDataType === 'incident' && (
              <div className="space-y-4 p-4 bg-red-50 rounded-lg border border-red-200">
                <h3 className="font-semibold text-red-800 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Saisie Incident
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Site *</Label>
                    <Select value={incidentForm.site_id} onValueChange={(v) => setIncidentForm({...incidentForm, site_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                      <SelectContent>
                        {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={incidentForm.incident_type} onValueChange={(v) => setIncidentForm({...incidentForm, incident_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="presqu_accident">Presqu'accident</SelectItem>
                        <SelectItem value="accident_sans_arret">Accident sans arrêt</SelectItem>
                        <SelectItem value="accident_avec_arret">Accident avec arrêt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Gravité</Label>
                    <Select value={incidentForm.severity} onValueChange={(v) => setIncidentForm({...incidentForm, severity: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mineur">Mineur</SelectItem>
                        <SelectItem value="modere">Modéré</SelectItem>
                        <SelectItem value="grave">Grave</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Jours d'arrêt</Label>
                    <Input type="number" value={incidentForm.days_lost} onChange={(e) => setIncidentForm({...incidentForm, days_lost: parseInt(e.target.value) || 0})} />
                  </div>
                  <div className="col-span-2">
                    <Label>Description *</Label>
                    <Textarea value={incidentForm.description} onChange={(e) => setIncidentForm({...incidentForm, description: e.target.value})} rows={2} />
                  </div>
                  <div className="col-span-2">
                    <Label>Cause</Label>
                    <Textarea value={incidentForm.cause} onChange={(e) => setIncidentForm({...incidentForm, cause: e.target.value})} rows={2} />
                  </div>
                </div>
              </div>
            )}
            
            {/* Action Form */}
            {addDataType === 'action' && (
              <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Saisie Action corrective
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Titre *</Label>
                    <Input value={actionForm.title} onChange={(e) => setActionForm({...actionForm, title: e.target.value})} />
                  </div>
                  <div>
                    <Label>Zone</Label>
                    <Select value={actionForm.zone_id} onValueChange={(v) => setActionForm({...actionForm, zone_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Optionnel" /></SelectTrigger>
                      <SelectContent>
                        {zones.map(z => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Responsable</Label>
                    <Select value={actionForm.responsible_id} onValueChange={(v) => setActionForm({...actionForm, responsible_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                      <SelectContent>
                        {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priorité</Label>
                    <Select value={actionForm.priority} onValueChange={(v) => setActionForm({...actionForm, priority: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="basse">Basse</SelectItem>
                        <SelectItem value="moyenne">Moyenne</SelectItem>
                        <SelectItem value="haute">Haute</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Statut</Label>
                    <Select value={actionForm.status} onValueChange={(v) => setActionForm({...actionForm, status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A_FAIRE">À faire</SelectItem>
                        <SelectItem value="EN_COURS">En cours</SelectItem>
                        <SelectItem value="FAIT">Fait</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>Description</Label>
                    <Textarea value={actionForm.description} onChange={(e) => setActionForm({...actionForm, description: e.target.value})} rows={2} />
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDataDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmitManualData} 
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventHistory;
